/**
 * Builds enhanced drug interaction response from WebSocket message.
 * Used by useWebSocketMessageHandler in two places to avoid duplicated logic.
 */

export interface SeverityDescriptionItem {
  description: string;
  drug_a?: string;
  drug_b?: string;
}

export interface EnhancedDrugInteractionResponse {
  intent?: string;
  current_medication_only_counts?: { high: number; moderate: number; minor: number };
  high_severity_descriptions?: SeverityDescriptionItem[];
  moderate_severity_descriptions?: SeverityDescriptionItem[];
  minor_severity_descriptions?: SeverityDescriptionItem[];
  /** Orchestration: draft line that triggered RTF / high-severity review (for focused UI). */
  high_severity_rtf_primary_medication_name?: string;
  /** When true, high-severity review flow finished — UI may show full multi-Rx summary again. */
  high_severity_prescription_review_resolved?: boolean;
}

/**
 * Converts raw drug_interactions array (with severity on each item) into UI shape (*_severity_descriptions).
 */
export function convertRawDrugInteractionsToSeverityDescriptions(
  rawDrugInteractionsArray: any[] | null | undefined
): {
  high_severity_descriptions: SeverityDescriptionItem[];
  moderate_severity_descriptions: SeverityDescriptionItem[];
  minor_severity_descriptions: SeverityDescriptionItem[];
} | null {
  if (!Array.isArray(rawDrugInteractionsArray) || rawDrugInteractionsArray.length === 0) return null;

  const high: SeverityDescriptionItem[] = [];
  const moderate: SeverityDescriptionItem[] = [];
  const minor: SeverityDescriptionItem[] = [];

  for (const item of rawDrugInteractionsArray) {
    const entry: SeverityDescriptionItem = {
      description: item.description ?? item.summary ?? "",
      drug_a: item.drug_a ?? item.drugA,
      drug_b: item.drug_b ?? item.drugB,
    };
    const severity = (item.severity || "").toString().toLowerCase();
    if (/^(high|severe)$/.test(severity)) high.push(entry);
    else if (/^moderate$/.test(severity)) moderate.push(entry);
    else minor.push(entry);
  }

  if (high.length === 0 && moderate.length === 0 && minor.length === 0) return null;
  return {
    high_severity_descriptions: high,
    moderate_severity_descriptions: moderate,
    minor_severity_descriptions: minor,
  };
}

/**
 * Builds enhanced drug interaction response from a WebSocket message.
 * Prefer message.data.drug_interaction_response; fallback to converting drug_interactions array and/or display counts.
 */
export function buildEnhancedDrugInteractionResponse(message: any): EnhancedDrugInteractionResponse | undefined {
  const rawDrugInteractionResponse = message?.data?.drug_interaction_response;
  const intentValue = message?.data?.intent;
  const rawDrugInteractionsArray =
    message?.data?.drug_interactions ?? message?.data?.drug_interaction_response_raw?.drug_interactions;
  const rawSummary =
    message?.data?.drug_interaction_response_raw?.summary ?? message?.data?.summary;
  const allMedicationsCounts =
    rawSummary?.ui_display ?? rawSummary?.all_medications ?? message?.data?.drug_interaction_response_raw?.ui_display;

  const displayCounts =
    allMedicationsCounts &&
    typeof allMedicationsCounts === "object" &&
    (allMedicationsCounts.high !== undefined ||
      allMedicationsCounts.moderate !== undefined ||
      allMedicationsCounts.minor !== undefined)
      ? {
          high: Number(allMedicationsCounts.high) || 0,
          moderate: Number(allMedicationsCounts.moderate) || 0,
          minor: Number(allMedicationsCounts.minor) || 0,
        }
      : undefined;

  const convertedFromDrugInteractions =
    convertRawDrugInteractionsToSeverityDescriptions(rawDrugInteractionsArray);

  const hasResult =
    rawDrugInteractionResponse || convertedFromDrugInteractions || displayCounts;
  if (!hasResult) return undefined;

  const nestedIntentRaw = (rawDrugInteractionResponse?.intent ?? "").toString().trim();
  const mergedIntent =
    nestedIntentRaw.length > 0
      ? nestedIntentRaw.toLowerCase()
      : (intentValue || "").toString().toLowerCase();

  const rtfPrimaryRaw = message?.data?.high_severity_rtf_primary_medication_name;
  const rtfPrimary =
    typeof rtfPrimaryRaw === "string" && rtfPrimaryRaw.trim().length > 0
      ? rtfPrimaryRaw.trim()
      : undefined;

  const hsResolvedRaw = message?.data?.high_severity_prescription_review_resolved;
  const hsReviewResolved =
    hsResolvedRaw === true ? true : hsResolvedRaw === false ? false : undefined;

  return {
    ...(rawDrugInteractionResponse || {}),
    ...(convertedFromDrugInteractions || {}),
    ...(displayCounts ? { current_medication_only_counts: displayCounts } : {}),
    intent: mergedIntent,
    ...(rtfPrimary ? { high_severity_rtf_primary_medication_name: rtfPrimary } : {}),
    high_severity_descriptions:
      rawDrugInteractionResponse?.high_severity_descriptions?.length
        ? rawDrugInteractionResponse.high_severity_descriptions
        : convertedFromDrugInteractions?.high_severity_descriptions,
    moderate_severity_descriptions:
      rawDrugInteractionResponse?.moderate_severity_descriptions?.length
        ? rawDrugInteractionResponse.moderate_severity_descriptions
        : convertedFromDrugInteractions?.moderate_severity_descriptions,
    minor_severity_descriptions:
      rawDrugInteractionResponse?.minor_severity_descriptions?.length
        ? rawDrugInteractionResponse.minor_severity_descriptions
        : convertedFromDrugInteractions?.minor_severity_descriptions,
    high_severity_rtf_primary_medication_name:
      rtfPrimary ??
      (typeof rawDrugInteractionResponse?.high_severity_rtf_primary_medication_name === "string"
        ? rawDrugInteractionResponse.high_severity_rtf_primary_medication_name.trim() || undefined
        : undefined),
    ...(hsReviewResolved !== undefined ? { high_severity_prescription_review_resolved: hsReviewResolved } : {}),
  };
}
